import { useState, useRef, useEffect } from "react";
import {
  HelpCircle,
  Loader2,
  ArrowRight,
  MapPin,
  Globe,
  Zap,
  MessageSquare,
  ChevronDown,
  Check,
} from "lucide-react";
import type { LegalQueryFormProps } from "../types";

// Common countries list
const COUNTRIES = [
  { code: "USA", name: "United States", flag: "🇺🇸" },
  { code: "GBR", name: "United Kingdom", flag: "🇬🇧" },
  { code: "CAN", name: "Canada", flag: "🇨🇦" },
  { code: "AUS", name: "Australia", flag: "🇦🇺" },
];

// US States list
const US_STATES = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
];

// Canadian Provinces
const CA_PROVINCES = [
  "Alberta",
  "British Columbia",
  "Manitoba",
  "New Brunswick",
  "Newfoundland and Labrador",
  "Nova Scotia",
  "Ontario",
  "Prince Edward Island",
  "Quebec",
  "Saskatchewan",
  "Northwest Territories",
  "Nunavut",
  "Yukon",
];

// Australian States
const AU_STATES = [
  "New South Wales",
  "Victoria",
  "Queensland",
  "Western Australia",
  "South Australia",
  "Tasmania",
  "Australian Capital Territory",
  "Northern Territory",
];


const getSubdivisionsForCountry = (countryCode: string): string[] => {
  switch (countryCode) {
    case "USA":
      return US_STATES;
    case "CAN":
      return CA_PROVINCES;
    case "AUS":
      return AU_STATES;
    default:
      return [];
  }
};

const EXAMPLE_QUESTIONS = [
  "Use vegetables from local farm in restaurant?",
  "Fly a drone over neighbor's property?",
  "Record calls without consent?",
  "Sell homemade food from kitchen?",
];

const LegalQueryForm = ({
  onSubmit,
  isResearching,
  loaderColor,
}: LegalQueryFormProps) => {
  const [query, setQuery] = useState("");
  const [country, setCountry] = useState("USA");
  const [subdivision, setSubdivision] = useState("");
  const [activeStep, setActiveStep] = useState(1);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showSubdivisionDropdown, setShowSubdivisionDropdown] = useState(false);

  const countryRef = useRef<HTMLDivElement>(null);
  const subdivisionRef = useRef<HTMLDivElement>(null);

  const subdivisions = getSubdivisionsForCountry(country);
  const selectedCountry = COUNTRIES.find((c) => c.code === country);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        countryRef.current &&
        !countryRef.current.contains(event.target as Node)
      ) {
        setShowCountryDropdown(false);
      }
      if (
        subdivisionRef.current &&
        !subdivisionRef.current.contains(event.target as Node)
      ) {
        setShowSubdivisionDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim() || !country) return;
    await onSubmit({ query, country, subdivision });
  };

  const handleExampleClick = (example: string) => {
    setQuery(example);
    setActiveStep(2);
  };

  const handleCountryChange = (newCountry: string) => {
    setCountry(newCountry);
    setSubdivision("");
    setShowCountryDropdown(false);
  };

  const handleSubdivisionChange = (newSubdivision: string) => {
    setSubdivision(newSubdivision);
    setShowSubdivisionDropdown(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Step indicators */}
      <div className="flex items-center gap-2 mb-6">
        <div
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${activeStep >= 1 ? "bg-[#7C3AED]/20 text-[#A78BFA]" : "bg-[#1A1A2E] text-[#64748B]"}`}
        >
          <span className="w-5 h-5 rounded-full bg-[#7C3AED]/30 flex items-center justify-center text-[10px]">
            1
          </span>
          Question
        </div>
        <div className="w-8 h-px bg-[#2D2D4A]"></div>
        <div
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${activeStep >= 2 ? "bg-[#06B6D4]/20 text-[#06B6D4]" : "bg-[#1A1A2E] text-[#64748B]"}`}
        >
          <span className="w-5 h-5 rounded-full bg-[#06B6D4]/30 flex items-center justify-center text-[10px]">
            2
          </span>
          Location
        </div>
        <div className="w-8 h-px bg-[#2D2D4A]"></div>
        <div
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${query && country ? "bg-[#10B981]/20 text-[#10B981]" : "bg-[#1A1A2E] text-[#64748B]"}`}
        >
          <span className="w-5 h-5 rounded-full bg-[#10B981]/30 flex items-center justify-center text-[10px]">
            3
          </span>
          Analyze
        </div>
      </div>

      {/* Main input area - asymmetric card */}
      <div className="relative">
        <div className="absolute -inset-px bg-gradient-to-r from-[#7C3AED]/50 via-transparent to-[#06B6D4]/50 rounded-2xl blur-sm opacity-50"></div>
        <div className="relative bg-[#0F0F1A] border-2 border-[#2D2D4A] rounded-2xl p-6 hover:border-[#3D3D5C] transition-colors">
          {/* Question section */}
          <div className="flex gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#7C3AED]/20 to-[#7C3AED]/5 flex items-center justify-center flex-shrink-0 border border-[#7C3AED]/20">
              <MessageSquare
                className="h-5 w-5 text-[#A78BFA]"
                strokeWidth={1.5}
              />
            </div>
            <div className="flex-1">
              <label className="text-[#64748B] text-xs uppercase tracking-wider mb-2 block">
                Your Question
              </label>
              <textarea
                required
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  if (e.target.value) setActiveStep(2);
                }}
                onFocus={() => setActiveStep(1)}
                rows={2}
                className="w-full bg-transparent text-[#E2E8F0] text-lg placeholder:text-[#3D3D5C] focus:outline-none resize-none"
                placeholder="Is it legal to..."
              />
            </div>
          </div>

          {/* Divider */}
          <div className="my-4 flex items-center gap-3">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#2D2D4A] to-transparent"></div>
            <span className="text-[10px] uppercase tracking-widest text-[#3D3D5C]">
              or try
            </span>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#2D2D4A] to-transparent"></div>
          </div>

          {/* Quick examples - horizontal scroll */}
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {EXAMPLE_QUESTIONS.map((example, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleExampleClick(example)}
                className="flex-shrink-0 px-3 py-2 rounded-lg bg-[#1A1A2E] text-[#94A3B8] text-sm hover:bg-[#252542] hover:text-[#E2E8F0] border border-[#2D2D4A] hover:border-[#7C3AED]/30 transition-all whitespace-nowrap"
              >
                <Zap className="inline h-3 w-3 mr-1.5 text-[#F59E0B]" />
                {example}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Location selection - split cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        {/* Country selector - larger */}
        <div ref={countryRef} className="md:col-span-3 relative">
          <button
            type="button"
            onClick={() => {
              setShowCountryDropdown(!showCountryDropdown);
              setShowSubdivisionDropdown(false);
              setActiveStep(2);
            }}
            className={`w-full bg-[#0F0F1A] border-2 rounded-xl p-4 transition-colors text-left ${showCountryDropdown ? "border-[#06B6D4]" : "border-[#2D2D4A] hover:border-[#06B6D4]/30"}`}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#06B6D4]/20 to-[#06B6D4]/5 flex items-center justify-center border border-[#06B6D4]/20">
                <Globe className="h-4 w-4 text-[#06B6D4]" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-[#64748B] text-xs uppercase tracking-wider block mb-1">
                  Country
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-[#E2E8F0] text-base truncate">
                    {selectedCountry?.name || "Select country"}
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 text-[#64748B] transition-transform flex-shrink-0 ${showCountryDropdown ? "rotate-180" : ""}`}
                  />
                </div>
              </div>
              {selectedCountry && (
                <span className="text-3xl flex-shrink-0">
                  {selectedCountry.flag}
                </span>
              )}
            </div>
          </button>

          {/* Country dropdown */}
          {showCountryDropdown && (
            <div className="absolute z-50 top-full left-0 right-0 mt-2 bg-[#0F0F1A] border-2 border-[#2D2D4A] rounded-xl overflow-hidden shadow-xl max-h-[60vh] overflow-y-auto scrollbar-hide">
              {COUNTRIES.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => handleCountryChange(c.code)}
                  className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-[#1A1A2E] transition-colors ${country === c.code ? "bg-[#7C3AED]/10" : ""}`}
                >
                  <span className="text-xl">{c.flag}</span>
                  <span className="text-[#E2E8F0] text-sm flex-1 text-left">
                    {c.name}
                  </span>
                  {country === c.code && (
                    <Check className="h-4 w-4 text-[#7C3AED]" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Subdivision selector - smaller */}
        <div ref={subdivisionRef} className="md:col-span-2 relative">
          <button
            type="button"
            onClick={() => {
              if (subdivisions.length > 0) {
                setShowSubdivisionDropdown(!showSubdivisionDropdown);
                setShowCountryDropdown(false);
              }
            }}
            className={`w-full bg-[#0F0F1A] border-2 rounded-xl p-4 transition-colors text-left ${showSubdivisionDropdown ? "border-[#06B6D4]" : "border-[#2D2D4A] hover:border-[#06B6D4]/30"} ${subdivisions.length === 0 ? "cursor-default" : ""}`}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#06B6D4]/10 to-transparent flex items-center justify-center border border-[#2D2D4A]">
                <MapPin className="h-4 w-4 text-[#64748B]" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-[#64748B] text-xs uppercase tracking-wider block mb-1">
                  {subdivisions.length > 0 ? "State/Province" : "Region"}
                </span>
                <div className="flex items-center gap-2">
                  {subdivisions.length > 0 ? (
                    <>
                      <span className="text-[#E2E8F0] text-base truncate">
                        {subdivision || "All regions"}
                      </span>
                      <ChevronDown
                        className={`h-4 w-4 text-[#64748B] transition-transform flex-shrink-0 ${showSubdivisionDropdown ? "rotate-180" : ""}`}
                      />
                    </>
                  ) : (
                    <input
                      type="text"
                      value={subdivision}
                      onChange={(e) => setSubdivision(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      placeholder="Optional"
                      className="w-full bg-transparent text-[#E2E8F0] text-base focus:outline-none placeholder:text-[#3D3D5C]"
                    />
                  )}
                </div>
              </div>
            </div>
          </button>

          {/* Subdivision dropdown */}
          {showSubdivisionDropdown && subdivisions.length > 0 && (
            <div className="absolute z-50 top-full left-0 right-0 mt-2 bg-[#0F0F1A] border-2 border-[#2D2D4A] rounded-xl overflow-hidden shadow-xl max-h-[60vh] overflow-y-auto scrollbar-hide">
              <button
                type="button"
                onClick={() => handleSubdivisionChange("")}
                className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-[#1A1A2E] transition-colors ${!subdivision ? "bg-[#7C3AED]/10" : ""}`}
              >
                <span className="text-[#E2E8F0] text-sm flex-1 text-left">
                  All regions
                </span>
                {!subdivision && <Check className="h-4 w-4 text-[#7C3AED]" />}
              </button>
              {subdivisions.map((sub) => (
                <button
                  key={sub}
                  type="button"
                  onClick={() => handleSubdivisionChange(sub)}
                  className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-[#1A1A2E] transition-colors ${subdivision === sub ? "bg-[#7C3AED]/10" : ""}`}
                >
                  <span className="text-[#E2E8F0] text-sm flex-1 text-left">
                    {sub}
                  </span>
                  {subdivision === sub && (
                    <Check className="h-4 w-4 text-[#7C3AED]" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Submit button - unique shape */}
      <div className="flex justify-end pt-2">
        <button
          type="submit"
          disabled={isResearching || !query.trim() || !country}
          className="group relative overflow-hidden px-8 py-4 rounded-xl bg-[#0F0F1A] border-2 border-[#7C3AED] text-[#E2E8F0] font-semibold transition-all hover:bg-[#7C3AED]/10 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
        >
          {/* Animated gradient on hover */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#7C3AED]/0 via-[#7C3AED]/20 to-[#7C3AED]/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>

          <span className="relative flex items-center gap-2">
            {isResearching ? (
              <>
                <Loader2
                  className="h-5 w-5 animate-spin"
                  style={{ stroke: loaderColor }}
                />
                <span>Analyzing</span>
                <span className="flex gap-0.5">
                  <span
                    className="w-1 h-1 rounded-full bg-[#A78BFA] animate-bounce"
                    style={{ animationDelay: "0ms" }}
                  ></span>
                  <span
                    className="w-1 h-1 rounded-full bg-[#A78BFA] animate-bounce"
                    style={{ animationDelay: "150ms" }}
                  ></span>
                  <span
                    className="w-1 h-1 rounded-full bg-[#A78BFA] animate-bounce"
                    style={{ animationDelay: "300ms" }}
                  ></span>
                </span>
              </>
            ) : (
              <>
                <HelpCircle className="h-5 w-5 text-[#A78BFA]" />
                Check Legality
                <ArrowRight className="h-4 w-4 text-[#A78BFA] group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </span>
        </button>
      </div>
    </form>
  );
};

export default LegalQueryForm;
